//#region Imports
import {
  AnydoAuthError,
  createHiddenInput,
  getPlatformForApi,
  SETTINGS_AUTH_FACEBOOK_LOGIN_URL,
  SETTINGS_AUTH_GOOGLE_LOGIN_URL,
  SETTINGS_FACEBOOK_APP_ID,
  SETTINGS_FACEBOOK_PERMISSIONS,
} from "@anydo/core";
import { AuthStatusCode, IAnydoAuth } from "@anydo/interfaces";
import { ABSERVICE_EXPERIMENTS } from "@anydo/ab-service";
import { createAuthFetchLoginInput } from "..";
import { fetchAuth } from "./fetchAuth";
import { getReferral } from "./utils/getReferral";
import { getFacebookToken } from "./utils/getFacebookToken";
import {
  AUTH_GOOGLE_OAUTH_QUERY_PARAM,
  AUTH_FACEBOOK_OAUTH_QUERY_PARAM,
  AUTH_GOOGLE_REDIRECT_SCOPE,
} from "./auth-constants";
import { redirectLoginWithApple } from "./redirectLoginWithApple";
import { getGoogleTokenByGoogleLogin } from "./google/google-ops";
import { AuthBase, AuthBaseOptions } from "./AuthBase";
import type { IAuthLoginDto } from "./auth-response-types";
//#endregion

interface GoogleAuthOptions {
  clientId: string;
}

export interface AuthBrowserOptions extends AuthBaseOptions {
  google: GoogleAuthOptions;
}

export class AuthBrowser extends AuthBase implements IAnydoAuth {
  private _google: GoogleAuthOptions;

  constructor({ google, ...options }: AuthBrowserOptions) {
    super(options);
    this._google = google;
  }

  //#region Public - Facebook login
  async redirectLoginWithFacebook() {
    const BASE_FB_OAUTH_URI = "https://www.facebook.com/dialog/oauth";
    // OAUTH allowed URL are absolute URL. So we omit all params and paths
    const redirectUri = `${window.location.origin}/?${AUTH_FACEBOOK_OAUTH_QUERY_PARAM}=1`;

    const params = new URLSearchParams();
    params.set("client_id", SETTINGS_FACEBOOK_APP_ID);
    params.set("redirect_uri", redirectUri);
    params.set("scope", SETTINGS_FACEBOOK_PERMISSIONS);
    params.set("response_type", "token");
    if (typeof window.location !== "undefined") {
      params.set("state", window.location.search);
    }

    window.location.href = `${BASE_FB_OAUTH_URI}/?${params.toString()}`;
  }

  async finishFacebookRedirectLogin(locationHash: string) {
    await this._runLoginFlow(() => {
      // NOTE: We remove the "#" from the params
      const facebookAuthToken = new URLSearchParams(locationHash.slice(1)).get("access_token");

      if (facebookAuthToken == null) {
        throw new AnydoAuthError(AuthStatusCode.THIRD_PARTY_ERROR, "Facebook redirect login failed");
      }

      return fetchAuth<IAuthLoginDto>(
        SETTINGS_AUTH_FACEBOOK_LOGIN_URL,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          access_token: facebookAuthToken,
          referrer: getReferral(),
        }),
      );
    });
  }

  async loginWithFacebook(): Promise<void> {
    await this._runLoginFlow(async () => {
      try {
        var facebookAuthToken = await getFacebookToken();
      } catch (e) {
        throw new AnydoAuthError(AuthStatusCode.THIRD_PARTY_ERROR, (e as Error).message);
      }

      return fetchAuth<IAuthLoginDto>(
        SETTINGS_AUTH_FACEBOOK_LOGIN_URL,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          access_token: facebookAuthToken,
          referrer: getReferral(),
        }),
      );
    });
  }
  //#endregion

  //#region Public - Google login
  async redirectLoginWithGoogle(): Promise<void> {
    const BASE_GOOGLE_OAUTH_URI = "https://accounts.google.com/o/oauth2/v2/auth";
    // OAUTH allowed URL are absolute URL. So we omit all params and paths
    const redirectUri = `${window.location.origin}/?${AUTH_GOOGLE_OAUTH_QUERY_PARAM}=1`;

    // NOTE: Direct URL doesn't work so we use form as in the instractions https://developers.google.com/identity/protocols/OAuth2UserAgent
    const form = document.createElement("form");
    form.setAttribute("method", "GET");
    form.setAttribute("action", BASE_GOOGLE_OAUTH_URI);
    form.appendChild(createHiddenInput("client_id", this._google.clientId));
    form.appendChild(createHiddenInput("redirect_uri", redirectUri));
    form.appendChild(createHiddenInput("response_type", "token"));
    form.appendChild(createHiddenInput("scope", AUTH_GOOGLE_REDIRECT_SCOPE));

    if (typeof window.location !== "undefined") {
      form.appendChild(createHiddenInput("state", window.location.search));
    }

    if (document.body != null) {
      document.body.appendChild(form);
      form.submit();
    } else {
      throw new ReferenceError("Auth#redirectLoginWithGoogle(): body element doesn't exit");
    }
  }

  async finishGoogleRedirectLogin(locationHash: string): Promise<void> {
    await this._runLoginFlow(async () => {
      const googleAuthToken = new URLSearchParams(locationHash.slice(1)).get("access_token");
      if (!googleAuthToken) {
        // NOTE: Technically this error never happens in current google login with redirect flow
        throw new AnydoAuthError(
          AuthStatusCode.THIRD_PARTY_ERROR,
          "Auth#finishGoogleRedirectLogin() - No user was logged in",
        );
      }

      return fetchAuth<IAuthLoginDto>(
        SETTINGS_AUTH_GOOGLE_LOGIN_URL,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          access_token: googleAuthToken,
          referrer: getReferral(),
        }),
      );
    });
  }

  async loginWithGoogle(): Promise<void> {
    await this._runLoginFlow(async () => {
      try {
        var googleAuthToken = await getGoogleTokenByGoogleLogin({
          clientId: this._google.clientId,
        });
      } catch (e) {
        throw new AnydoAuthError(AuthStatusCode.THIRD_PARTY_ERROR, (e as Error).message);
      }
      return fetchAuth<IAuthLoginDto>(
        SETTINGS_AUTH_GOOGLE_LOGIN_URL,
        createAuthFetchLoginInput({
          client_id: this._clientId,
          locale: this._i18nLocaleLoader.localeData.locale,
          access_token: googleAuthToken,
          referrer: getReferral(),
        }),
      );
    });
  }
  //#endregion

  //#region Public - Apple
  async loginWithApple(): Promise<void> {
    throw new Error("Auth#loginWithApple() - Shouldn't be implemented in Browser");
  }

  async redirectLoginWithApple() {
    try {
      await redirectLoginWithApple({
        platform: getPlatformForApi(),
        referrer: getReferral(),
        locale: this._i18nLocaleLoader.localeData.locale,
        clientId: this._clientId,
        experiments: ABSERVICE_EXPERIMENTS,
      });
    } catch (e) {
      throw new AnydoAuthError(AuthStatusCode.THIRD_PARTY_ERROR, (e as Error).message);
    }
  }
  //#endregion
}
